// What the reviewer sent back, as ONE card at the top of the editor: who, when, their note, the
// changes they rejected (already reverted in this copy), what was accepted (already applied),
// and any per-field comments. A plain card like the rest of the page — this is feedback to act
// on, not an alert — so it uses the standard surface, navy headings and muted meta.
import { formatServerDateShort } from '@/lib/dates';
import { ClampedText } from '@/components/ClampedText';
import { changeLabel, isPictureChange } from '../reviewDecisions';
import type { ReviewComment, ReviewDecisionSummary, ReviewThreadEntry } from '../collabTypes';

export interface SendBackNoticeProps {
  note: string | null;
  comments: ReviewComment[];
  decisions: ReviewDecisionSummary[];
  /** The review thread; the most recent send-back entry gives the reviewer's name and time. */
  thread: ReviewThreadEntry[];
}

/** Nothing to show unless the copy was actually sent back with something to say. */
export function hasSendBackFeedback(p: Pick<SendBackNoticeProps, 'note' | 'comments' | 'decisions'>): boolean {
  return !!p.note || p.comments.length > 0 || p.decisions.length > 0;
}

export function SendBackNotice({ note, comments, decisions, thread }: SendBackNoticeProps) {
  const sentBack = [...thread].reverse().find((e) => e.kind === 'reject') ?? null;
  const reviewer = sentBack?.author_name || sentBack?.author_email || null;
  const rejected = decisions.filter((d) => d.decision === 'reject');
  const accepted = decisions.filter((d) => d.decision === 'accept').length;
  // The reviewer's comments about a specific change, keyed by that change's id.
  const commentsFor = new Map<number, string[]>();
  for (const e of thread) {
    if (e.kind === 'comment' && e.revision_id !== null) {
      commentsFor.set(e.revision_id, [...(commentsFor.get(e.revision_id) ?? []), e.body]);
    }
  }

  return (
    <section
      aria-label="Sent back for changes"
      className="mb-6 rounded-2xl border border-line bg-white px-5 py-4 shadow-card"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-[14px] font-semibold text-navy">Sent back for changes</h3>
        {sentBack && (
          <span className="text-[12px] text-muted">
            {reviewer ? `${reviewer} · ` : ''}{formatServerDateShort(sentBack.at)}
          </span>
        )}
      </div>

      {note && (
        <div className="mt-2">
          <ClampedText className="text-[13.5px] leading-relaxed text-navy" lines={5}>{note}</ClampedText>
        </div>
      )}

      {rejected.length > 0 && (
        <div className="mt-4">
          <p className="text-[13px] font-semibold text-navy">
            {rejected.length} change{rejected.length === 1 ? '' : 's'} to rework
          </p>
          <p className="mt-0.5 text-[12.5px] text-muted">
            Rejected by the reviewer and already reverted here — the text is back as it was.
          </p>
          <ol className="mt-3 space-y-3">
            {rejected.map((d, i) => (
              <li key={d.revision_id} className="flex gap-3">
                <span
                  aria-hidden
                  className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-navy text-[11px] font-semibold tabular-nums text-white"
                >
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] leading-relaxed text-navy">
                    <span className="text-muted">
                      {changeLabel(d.change_type)}{isPictureChange({ text: d.change_text }) ? ' a picture' : ''}
                    </span>{' '}
                    <span className="[overflow-wrap:anywhere]">&ldquo;{d.change_text}&rdquo;</span>
                  </p>
                  {(commentsFor.get(d.revision_id) ?? []).map((body, ci) => (
                    <p
                      key={ci}
                      className="mt-1.5 border-l-2 border-line-strong pl-3 text-[12.5px] leading-relaxed text-navy/75 [overflow-wrap:anywhere]"
                    >
                      {reviewer && <span className="font-medium text-navy">{reviewer}: </span>}
                      {body}
                    </p>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {accepted > 0 && (
        <p className="mt-4 border-t border-line pt-3 text-[12.5px] text-muted">
          The reviewer accepted {accepted} other change{accepted === 1 ? '' : 's'} — already applied
          here as ordinary text, nothing to do.
        </p>
      )}

      {comments.length > 0 && (
        <ul className="mt-3 space-y-1.5 border-t border-line pt-3">
          {comments.map((c, i) => (
            <li key={`${c.path}-${i}`} className="text-[13px] text-navy">
              <span className="font-semibold">{c.field}:</span>{' '}
              <span className="text-muted [overflow-wrap:anywhere]">{c.comment}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
